import { gql } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, FormControlLabel, Switch } from '@mui/material';
import Location from './Location';
import { blue } from '@mui/material/colors';
import AutoContent from './AutoContent';
const USER_NAVBAR = gql`
query accountNavbar($sensor: String) {
  observation(criteria:{sensor: $sensor}) {
    countAvailable
    observations {
      title
      uri
      contentType
      content
      extent
    }
  }
}`;

const PLACE_NAVBAR = gql`
query placeNavbar($locator:Locator!, $sensor: String) {
  scanPlace(locator:$locator, criteria:{sensors: [$sensor], contentTypeExclusion: "image/*"}) {
    countAvailable
    observations {
      title
      uri
      contentType
      content
    }
  }
}
`;

const SEND_TELEMETRY = gql`
mutation sendTelemetry($longitude: Float, $latitude: Float) {
  sendTelemetry(telemetry: {longitude: $longitude, latitude: $latitude})
}
`;

const CHECK_TELEMETRY_INTERVAL = 5000;
const SEND_TELEMETRY_INTERVAL = 30000;
const UPDATE_NAVBAR_INTERVAL = 30000;

const SendTelemetry = props => {
  const intervalId = useRef(null)
  const lastTelemetrySentTime = useRef(null)

  const sendTelemetry = (client, onError) => {
    Location.getGPSLocation(
      position => {
        props.client.mutate({
          mutation: SEND_TELEMETRY,
          variables: { longitude: position.coords.longitude, latitude: position.coords.latitude },
          refetchQueries: ['TravelInformation']
        }).then(data => {
          if (!data.data.sendTelemetry) {
            console.log('Send telemetry failed');
          } else {
            console.log('Sent telemetry')
          }
          lastTelemetrySentTime.current = new Date().getTime()
        }).catch(err => {
          lastTelemetrySentTime.current = new Date().getTime()
          onError(err)
        });
      },
      error => {
        //Do not call onError, this can be sent later
        console.log(`Geolocation failed, ${error}`)
        lastTelemetrySentTime.current = new Date().getTime()
      }
    )
  }

  const checkTelemetry = () => {
    console.log("Check")
    const now = new Date().getTime()

    if (!props.account) {
      console.log("Don't send telemetry, not logged in")
      return
    } 

    if (!Location.isGpsEnabled()) {
      console.log("GPS Disabled, do not send telmetry")
      return
    }

    if (lastTelemetrySentTime.current !== null) {
      var nextTelemetryTime = lastTelemetrySentTime.current + SEND_TELEMETRY_INTERVAL
      if (nextTelemetryTime > now) {
        console.log("Too soon to send telemetry")
        return
      }
    }

    sendTelemetry(props.client, props.onError)
  }

  useEffect(() => {
    if (props.disabled) {
      return
    }

    if (props.enabled || true) {
      // Start the timer when enabled becomes true
      intervalId.current = setInterval(checkTelemetry, CHECK_TELEMETRY_INTERVAL); // Execute track periodically
      checkTelemetry()
    } else {
      // Clear the timer when enabled becomes false
      clearInterval(intervalId.current);
      intervalId.current = null; // Reset the ref
    }

    // Cleanup function to clear the interval when the component unmounts
    return () => {
      clearInterval(intervalId.current);
    };
  }, [props.enabled]); // Re-run the effect only when the 'tracking' state changes
}

const LoadSection = props => {
  const intervalId = useRef(null)
  const sensor = props.sensor

  const update = () => {
    console.log(`Update ${sensor}`)
    
    props.client.query(
      {
        query: USER_NAVBAR,
        fetchPolicy: 'network-only',
        variables: { sensor },
        notifyOnNetworkStatusChange: true
      }).then(userData => {
          const userRecords = userData?.data?.observation?.observations
          if (userRecords?.length > 0) {
            console.log(`Loaded user ${sensor}`)
            props.onLoad(userRecords[0])
          } else {
            console.log(`User ${sensor} not available, scanning place`)
            props.client.query(
              {
                query: PLACE_NAVBAR,
                fetchPolicy: 'network-only',
                variables: {
                  locator: { useRequestIpAddress: true },
                  sensor
                },
                notifyOnNetworkStatusChange: true
              }).then(placeData => {
                  const placeRecords = placeData?.data?.scanPlace?.observations
                  if (placeRecords?.length > 0) {
                    props.onLoad(placeRecords[0])
                    console.log(`loaded place ${sensor}`)
                  } else {
                    props.onLoad(null)
                    console.log(`${sensor} not available`)
                  }
                }
            )
          }
        }
    )
  }

  useEffect(() => {
    if (props.enabled) {
      // Start the timer when enabled becomes true
      intervalId.current = setInterval(update, UPDATE_NAVBAR_INTERVAL); // Execute track periodically
      update()
    } else {
      // Clear the timer when enabled becomes false
      clearInterval(intervalId.current);
      intervalId.current = null; // Reset the ref
    }

    // Cleanup function to clear the interval when the component unmounts
    return () => {
      clearInterval(intervalId.current);
    };
  }, [props.enabled]); // Re-run the effect only when the 'tracking' state changes
}


const Navbar = props => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [highlight, setHighlight] = useState(null)
  const [url, setUrl] = useState(null)
  const open = Boolean(anchorEl)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  }

  const handleClose = () => {
    setAnchorEl(null);
  }

  const resolved = highlight?.content

  const buttonSx = {
  // Common attributes
  borderRadius: '1em',

  // large
  ...(props.large
    ? {fontSize: '68px'}
    : {}
  ),

  // Conditional attributes
  ...(resolved
    ? {
        bgcolor: blue[400],
        '&:hover': {
          bgcolor: blue[300],
        },
      }
    : {
        bgcolor: blue[300],
        '&:hover': {
          bgcolor: blue[200],
        },
      }),
  };

  return (<div>
    <SendTelemetry account={props.account} client={props.client} />
    <LoadSection sensor='navbar.highlight' client={props.client} account={props.account} enabled={true} onLoad={setHighlight} />
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ m: 1, position: 'relative' }}>
        {/* <div className={resolved ? '' : 'thinking'}> */}
        <div>
          <Button
            className='pill'
            variant='contained'
            size={props.size || 'small'}
            aria-label='Get location'
            color='primary'
            sx={buttonSx}
            onMouseOver={() => { }}
            onMouseOut={() => { }}
            onClick={handleClick}
          >
            <NavbarHighlight client={props.client} highlight={highlight} />
          </Button>
        </div>
      </Box>
    </Box>
    <Dialog
      id="navbar-menu"
      open={open}
      onClose={handleClose}
      slotProps={{
        list: {
          'aria-labelledby': 'basic-button',
        },
      }}
    >
        <DialogContent>
          <NavbarDetails client={props.client} setUrl={setUrl} />
          <FormControlLabel control={
            <Switch
              disabled={!props.account}
              checked={props.gpsEnabled}
              onChange={() => {
                props.setGpsEnabled(!props.gpsEnabled)
              }}
            />
          } label="GPS" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { window.location.href = url }}>More</Button>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
    </Dialog>
  </div>
  )
}

const NavbarHighlight = props => {
  return (<span>
    {
      props.highlight?.content || "⌛"
    }
  </span>)
}

const NavbarDetails = props => {
  const [details, setDetails] = useState(null)

  if (details) {
    props.setUrl(details.uri)
  }

  return (<div onClick={() => window.location.href = details.uri}>
    <LoadSection sensor='navbar.details' client={props.client} account={props.account} enabled={true} onLoad={setDetails} />
    <b>{details?.title}</b>
    <AutoContent content={details?.content} contentType={details?.contentType} client={props.client} />
  </div>)
}

export default Navbar